// Shared tiny admin UI helpers
import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronUp, ChevronDown } from "lucide-react";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl font-semibold">{title}</h1>
        {subtitle && <p className="text-sm text-[#6C757D]">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}

export function PrimaryButton({ children, className = "", ...rest }) {
  return (
    <button className={`bg-[#212529] text-white rounded-md px-4 py-2 text-sm hover:bg-[#343A40] disabled:opacity-60 ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function SecondaryButton({ children, className = "", ...rest }) {
  return (
    <button className={`bg-white border border-[#E9ECEF] text-[#212529] rounded-md px-4 py-2 text-sm hover:bg-[#F1F3F5] ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function DangerButton({ children, className = "", ...rest }) {
  return (
    <button className={`bg-white border border-[#D0021B]/40 text-[#D0021B] rounded-md px-3 py-1.5 text-xs hover:bg-[#D0021B]/5 ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function LinkButton({ to, children, className = "" }) {
  return (
    <Link to={to} className={`bg-[#212529] text-white rounded-md px-4 py-2 text-sm hover:bg-[#343A40] inline-block ${className}`}>
      {children}
    </Link>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-[#6C757D] mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-[#6C757D] mt-1">{hint}</span>}
    </label>
  );
}

export function Input(props) {
  return <input {...props} className={`w-full h-10 px-3 border border-[#E9ECEF] rounded-md focus:outline-none focus:border-[#212529] text-sm ${props.className || ""}`} />;
}

export function Textarea(props) {
  return <textarea {...props} className={`w-full px-3 py-2 border border-[#E9ECEF] rounded-md focus:outline-none focus:border-[#212529] text-sm ${props.className || ""}`} />;
}

export function Select(props) {
  // appearance-none hides the native chevron so we can position a custom one
  // with a comfortable right-side gap (the native arrow sits flush against
  // the border on most browsers, which looks cramped).
  const chevron =
    "bg-[url('data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20stroke%3D%22%236C757D%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%208%2010%2012%2014%208%22/%3E%3C/svg%3E')] bg-no-repeat bg-[length:16px_16px] bg-[position:right_0.75rem_center]";
  return (
    <select
      {...props}
      className={`w-full h-10 pl-3 pr-9 border border-[#E9ECEF] rounded-md focus:outline-none focus:border-[#212529] text-sm bg-white appearance-none ${chevron} ${props.className || ""}`}
    >
      {props.children}
    </select>
  );
}

export function Card({ children, className = "" }) {
  return <div className={`bg-white border border-[#E9ECEF] rounded-lg p-4 ${className}`}>{children}</div>;
}

export function Table({ children, className = "" }) {
  return <table className={`w-full text-sm ${className}`}>{children}</table>;
}

export function Thead({ columns }) {
  return (
    <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF] text-left text-xs uppercase tracking-wider text-[#6C757D]">
      <tr>{columns.map((c) => <th key={c} className="px-3 py-2.5 font-medium">{c}</th>)}</tr>
    </thead>
  );
}

// Clickable column header. Pass `sortKey=null` (or omit `sortKey`) for non-sortable
// columns (action / image / decorative). Sort cycle is asc → desc → unsorted.
export function SortableHeader({ label, sortKey, sort, onSort, align = "left", className = "" }) {
  if (!sortKey) {
    return (
      <th
        className={`px-3 py-2.5 font-medium ${align === "right" ? "text-right" : "text-left"} ${className}`}
      >
        {label}
      </th>
    );
  }
  const active = sort?.key === sortKey;
  const dir = active ? sort.dir : null;
  return (
    <th
      className={`px-3 py-2.5 font-medium cursor-pointer select-none hover:text-ink ${align === "right" ? "text-right" : "text-left"} ${className}`}
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      <span className={`inline-flex items-center gap-1 ${active ? "text-ink" : ""}`}>
        {label}
        {dir === "asc" && <ChevronUp className="w-3 h-3" />}
        {dir === "desc" && <ChevronDown className="w-3 h-3" />}
      </span>
    </th>
  );
}

// Shared 3-state sort hook used by every admin list table.
// `getValue(row, key)` returns the comparable value for that column — strings
// localeCompare, numbers subtract, nulls always sink to the bottom.
export function useTableSort(initial = { key: null, dir: null }) {
  const [sort, setSort] = useState(initial);
  function toggleSort(key) {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null };
    });
  }
  function sortRows(rows, getValue) {
    if (!sort.key || !sort.dir) return rows;
    const dirMul = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = getValue(a, sort.key);
      const bv = getValue(b, sort.key);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dirMul;
      return String(av).localeCompare(String(bv)) * dirMul;
    });
  }
  return { sort, toggleSort, sortRows };
}

export function StatusBadge({ status }) {
  const map = {
    new: "bg-blue-50 text-blue-700",
    read: "bg-gray-100 text-gray-700",
    pending_review: "bg-gray-100 text-gray-700",
    converted_to_clothing_item: "bg-green-50 text-green-700",
    ignored: "bg-gray-200 text-gray-600",
    duplicate: "bg-gray-200 text-gray-600",
    error: "bg-red-50 text-red-700",
    under_review: "bg-yellow-50 text-yellow-700",
    confirmed: "bg-red-50 text-red-700",
    rejected: "bg-gray-200 text-gray-600",
    resolved: "bg-green-50 text-green-700",
    approved_featured: "bg-green-50 text-green-700",
    not_sustainable: "bg-red-50 text-red-700",
    needs_more_information: "bg-yellow-50 text-yellow-800",
    archived: "bg-gray-200 text-gray-600",
    queued: "bg-blue-50 text-blue-700",
    running: "bg-yellow-50 text-yellow-700",
    completed: "bg-green-50 text-green-700",
    completed_with_errors: "bg-yellow-50 text-yellow-700",
    failed: "bg-red-50 text-red-700",
    responded: "bg-green-50 text-green-700",
  };
  const labelMap = {
    approved_featured: "approved",
    not_sustainable: "not sustainable",
    needs_more_information: "needs info",
    pending_review: "pending review",
    converted_to_clothing_item: "converted",
    completed_with_errors: "completed with errors",
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs ${map[status] || "bg-gray-100 text-gray-700"}`}>{labelMap[status] || status}</span>;
}
